import { orderBy, sortBy } from 'lodash';
import { converters as tableConverters } from 'store/table';
import {
  IBaseSurvey,
  IFile,
  ISurvey,
  ISurveyAttachment,
  ISurveyGroupAssignment,
  ISurveyResults,
  IBaseSurveySubmission,
  ISurveySubmission,
  IBaseSurveyPendingSubmission,
} from 'types/income';
import { includes } from 'utils/general';
import { ContentLanguage } from 'types/general';
import { convertUploadedFiles } from 'store/files';
import { loaders } from './surveys.loaders';
import {
  BaseSurvey,
  Survey,
  surveyChoiceAnswerFormats,
  SurveyGroupAssignment,
  surveyRatingScaleAnswerTypes,
  SurveyResults,
  BaseSurveySubmission,
  SurveySubmission,
  BaseSurveyPendingSubmission,
} from './surveys.types';
import { OPTIONS_BY_RATING_SCALE } from './surveys.constants';

const isUrlAttachment = (
  attachment: ISurveyAttachment | IFile,
): attachment is Extract<ISurveyAttachment, { type: 'url' }> =>
  'type' in attachment && attachment.type === 'url';

export const splitSurveyAttachments = (
  attachments: Array<ISurveyAttachment | IFile> = [],
) => {
  const files: IFile[] = [];
  const videos: string[] = [];

  attachments.forEach((attachment) => {
    if (isUrlAttachment(attachment)) {
      videos.push(attachment.url);
      return;
    }

    files.push({
      id: attachment.id,
      signedUrl: attachment.signedUrl,
      mime: attachment.mime,
      fileName: attachment.fileName,
    });
  });

  return {
    attachments: convertUploadedFiles(files),
    videos,
  };
};

const convertBaseSurvey = (survey: IBaseSurvey): BaseSurvey => {
  return {
    id: survey.id,
    name: survey.name,
    type: survey.type,
    status: survey.status,
    notificationMethods: [
      ...(survey.emailNotificationsEnabled ? ['email' as const] : []),
      ...(survey.mobileNotificationsEnabled
        ? ['appNotification' as const]
        : []),
    ],
    invitationSentTo: survey.invitationSentTo,
    currentRecipients: survey.currentRecipients,
    completedBy: survey.completedBy,
    submittedBy: survey.submittedBy,
    passedBy: survey.passedBy,
    scoringEnabled: survey.scoringEnabled,
    minScore: survey.minScore ?? 0,
    retries: survey.retries ?? 0,
    rewardPoints: survey.rewardPoints ?? 0,
    anonymous: survey.anonymous,
    hasWebAccess: survey.hasWebAccess ?? false,
    creationDate: survey.creationDate,
    publishDateTime: survey.publishDateTime ?? null,
    endDateTime: survey.endDateTime ?? null,
  };
};

export const convertSurveyQuestions = (
  questions: ISurvey['questions'],
  supportedLanguages: Uppercase<ContentLanguage>[],
): Survey['questions'] => {
  return questions.map((question) => {
    const groupedOptionsByIndex = Object.groupBy(
      question.options,
      (option) => option.index,
    );

    return {
      ...question,
      isRequired: !question.isOptional,
      translations: sortBy(
        question.translations.map((trans) => {
          const { attachments, videos } = splitSurveyAttachments(
            trans.attachments,
          );

          return {
            ...trans,
            description: trans.description ?? '',
            imageUrl: trans.imageUrl ?? null,
            attachments,
            videos,
          };
        }),
        [(trans) => supportedLanguages.indexOf(trans.language)],
      ),
      options: Object.entries(groupedOptionsByIndex).map(
        ([index, translations]) => ({
          index: Number(index),
          translations: sortBy(
            translations?.map((trans) => ({
              language: trans.language,
              text: trans.text,
              imageId: trans.imageId,
              imageUrl: trans.imageUrl ?? null,
              isCorrect: trans.isCorrect ?? false,
            })) ?? [],
            [(trans) => supportedLanguages.indexOf(trans.language)],
          ),
        }),
      ),
      metadata: {
        withImage: question.metadata?.withImage ?? false,
        withDescription: question.metadata?.withDescription ?? false,
        withAttachments: question.metadata?.withAttachments ?? false,
        withVideo: question.metadata?.withVideo ?? false,
        choiceAnswerFormat:
          question.metadata?.choiceAnswerFormat ?? surveyChoiceAnswerFormats[0],
        conditionalJumpRules: question.metadata.conditionalJumpRules ?? [],
      },
    };
  });
};

const convertSurvey = (survey: ISurvey): Survey => {
  const supportedLanguages = survey.languages.map((trans) => trans.language);
  const translations = sortBy(
    survey.languages.map((trans) => {
      const { attachments, videos } = splitSurveyAttachments(trans.attachments);

      return {
        language: trans.language,
        title: trans.title,
        description: trans.description,
        imageId: trans.imageId,
        imageUrl: trans.imageUrl,
        attachments,
        videos,
      };
    }),
    [(trans) => supportedLanguages.indexOf(trans.language)],
  );
  const hasVideo = translations.some((trans) => trans.videos.length > 0);

  return {
    ...convertBaseSurvey(survey),
    questions: convertSurveyQuestions(survey.questions, supportedLanguages),
    translations,
    // Remove "survey.metadata ?" after a year, when all surveys will have metadata
    metadata: survey.metadata
      ? {
          withDescription: survey.metadata.withDescription ?? false,
          withImage: survey.metadata.withImage ?? false,
          withAttachments: survey.metadata.withAttachments ?? false,
          withVideo: survey.metadata.withVideo ?? false,
        }
      : {
          withDescription: !!survey.languages.find(
            (trans) => !!trans.description,
          ),
          withImage: !!survey.languages.find((trans) => !!trans.imageId),
          withAttachments: !!survey.languages.find((trans) =>
            trans.attachments.some(
              (attachment) => !isUrlAttachment(attachment),
            ),
          ),
          withVideo: hasVideo,
        },
  };
};

const convertSurveys = (surveys: IBaseSurvey[]): BaseSurvey[] => {
  return surveys.map(convertBaseSurvey);
};

const convertSurveyGroupAssignment = (
  assignment: ISurveyGroupAssignment,
): SurveyGroupAssignment => {
  return {
    id: assignment.groupId,
    groupId: assignment.groupId,
    groupName: assignment.groupName,
    employeeCount: assignment.employeeCount,
    assignmentStatus: assignment.isAssigned ? 'on' : 'off',
  };
};

const convertSurveyGroupAssignments = (
  assignments: ISurveyGroupAssignment[],
): SurveyGroupAssignment[] => {
  return assignments.map(convertSurveyGroupAssignment);
};

const convertSurveyResults = (survey: ISurveyResults): SurveyResults => {
  return {
    ...convertBaseSurvey(survey),
    questions: survey.questions.map((question) => {
      const supportedLanguages = question.translations.map(
        (trans) => trans.language,
      );
      const groupedOptionsByIndex = Object.groupBy(
        question.options,
        (option) => option.index,
      );

      return {
        ...question,
        answeredByCount: question.optionResponseStats.reduce(
          (acc, stat) => acc + stat.answeredByCount,
          0,
        ),
        optionResponseStats: question.optionResponseStats,
        translations: sortBy(
          question.translations.map((trans) => {
            const { attachments, videos } = splitSurveyAttachments(
              trans.attachments,
            );

            return {
              ...trans,
              description: trans.description ?? '',
              imageUrl: trans.imageUrl ?? null,
              attachments,
              videos,
            };
          }),
          [(trans) => supportedLanguages.indexOf(trans.language)],
        ),
        options: includes(surveyRatingScaleAnswerTypes, question.type)
          ? OPTIONS_BY_RATING_SCALE[question.type].map((option, i) => ({
              index: i,
              translations: supportedLanguages.map((language) => ({
                language,
                text: option,
                imageId: null,
                imageUrl: null,
                isCorrect: false,
              })),
            }))
          : Object.entries(groupedOptionsByIndex).map(
              ([index, translations]) => ({
                index: Number(index),
                translations: sortBy(
                  translations?.map((trans) => ({
                    ...trans,
                    imageUrl: trans.imageUrl ?? null,
                    isCorrect: trans.isCorrect ?? false,
                  })) ?? [],
                  [(trans) => supportedLanguages.indexOf(trans.language)],
                ),
              }),
            ),
      };
    }),
    anonymous: survey.anonymous,
  };
};

const convertBaseSurveySubmission = (
  submission: IBaseSurveySubmission,
): BaseSurveySubmission => {
  return {
    id: submission.id,
    employee: submission.employeeId
      ? { id: submission.employeeId, name: submission.employeeName }
      : null,
    company: submission.companyId
      ? { id: submission.companyId, name: submission.companyName }
      : null,
    department: submission.departmentName
      ? { name: submission.departmentName }
      : null,
    startDate: submission.startDate,
    completionDate: submission.completionDate ?? null,
    score: submission.score,
    submissionAttempt: submission.submissionAttempt,
  };
};

const convertSurveySubmission = (
  submission: ISurveySubmission,
): SurveySubmission => {
  return {
    ...convertBaseSurveySubmission(submission),
    submissionAttempt: submission.submissionAttempt,
    totalSubmissions: submission.totalSubmissions,
    answers: submission.answers,
  };
};

const convertSurveySubmissions = (
  submissions: IBaseSurveySubmission[],
): BaseSurveySubmission[] => {
  return submissions.map(convertBaseSurveySubmission);
};

const convertBaseSurveyPendingSubmission = (
  submission: IBaseSurveyPendingSubmission,
): BaseSurveyPendingSubmission => {
  return {
    id: submission.id,
    employee: submission.fullname ? { name: submission.fullname } : null,
    company: submission.companyId
      ? { id: submission.companyId, name: submission.companyName }
      : null,
    department: submission.departmentId
      ? { id: submission.departmentId, name: submission.departmentName }
      : null,
  };
};

const convertSurveyPendingSubmissions = (
  submissions: IBaseSurveyPendingSubmission[],
): BaseSurveyPendingSubmission[] => {
  return submissions.map(convertBaseSurveyPendingSubmission);
};

export const converters = {
  getSurveys: ({ data }: Awaited<ReturnType<typeof loaders.getSurveys>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertSurveys(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getSurvey: ({ data }: Awaited<ReturnType<typeof loaders.getSurvey>>) => {
    return convertSurvey(data);
  },
  getSurveyGroupAssignments: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getSurveyGroupAssignments>>) => {
    const items = convertSurveyGroupAssignments(data);

    return {
      items: orderBy(items, ['assignmentStatus'], ['desc']),
      total: data.length,
    };
  },
  getSurveyResults: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getSurveyResults>>) => {
    return convertSurveyResults(data);
  },
  getSurveyPendingSubmissions: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getSurveyPendingSubmissions>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertSurveyPendingSubmissions(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getSurveySubmissions: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getSurveySubmissions>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertSurveySubmissions(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getSurveySubmission: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getSurveySubmission>>) => {
    return convertSurveySubmission(data);
  },
};
